import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import moment from "moment";
import { createSelector } from "reselect";
import { makeStyles } from "@material-ui/core/styles";
import Map from "@mui/icons-material/Map";
import Button from "../../components/CustomButtons/Button.js";
import Snackbars from "../../components/Snackbar/Snackbar.js";
import CustomDropdown from "../../components/CustomDropdown/CustomDropdown.js";
import SelectDateMenu from "../../components/SelectDateMenu/SelectDateMenu";
import CardTable from "../../components/CardTable/CardTable";
import { convertToMapById } from "../../helpers/convertToMap";
import {
  getOrders,
  addOrdersSelected,
  removeOrdersSelected,
  changeStation,
  changeStartDate,
  changeEndDate,
  setError,
  clearError,
  clearSuccess,
} from "../../redux/ducks/ordersHistory";
import { configTable, menuOptions } from "./config";
import { styles } from "./styles";

dayjs.extend(isSameOrBefore);

const useStyles = makeStyles(styles);

const startDateSelector = createSelector(
  (state) => state.ordersHistory.startDate,
  (startDate) => (startDate ? moment(startDate, "YYYY-MM-DD") : moment())
);

const endDateSelector = createSelector(
  (state) => state.ordersHistory.endDate,
  (endDate) => (endDate ? moment(endDate, "YYYY-MM-DD") : moment())
);

export default function OrdersHistory() {
  const dispatch = useDispatch();

  const orders = useSelector((state) => state.ordersHistory.orders);
  const isLoadingOrders = useSelector(
    (state) => state.ordersHistory.isLoadingOrders
  );
  const { stations } = useSelector((state) => state.companies);
  const stationSelected = useSelector(
    (state) => state.ordersHistory.stationSelected
  );
  const startDate = useSelector((state) => startDateSelector(state));
  const endDate = useSelector((state) => endDateSelector(state));
  const { error, errorMessage } = useSelector(
    (state) => state.ordersHistory,
    shallowEqual
  );
  const { success, message } = useSelector(
    (state) => state.ordersHistory,
    shallowEqual
  );
  const colorSnackbar = useSelector(
    (state) => state.ordersHistory.colorSnackbar,
    shallowEqual
  );

  const [typeCustomer, setTypeCustomer] = useState({
    label: "Cliente",
    value: "CLIENTE",
  });

  const classes = useStyles();
  const [mapStations, setMapStations] = useState([]);

  const fetchOrders = useCallback(
    (stations, startDate, endDate, type) => {
      dispatch(getOrders(stations, startDate, endDate, type));
    },
    []
  );

  const addOrders = useCallback(
    (orders) => {
      dispatch(addOrdersSelected(orders));
    },
    []
  );

  const removeOrders = useCallback(
    (orders) => {
      dispatch(removeOrdersSelected(orders));
    },
    []
  );

  const changeStationMemoized = useCallback(
    (station) => {
      dispatch(changeStation(station));
    },
    []
  );

  const changeTypeCustomer = (target) => {
    setTypeCustomer(target);
  };

  const changeStartDateMemoized = useCallback(
    (date) => {
      dispatch(changeStartDate(dayjs(date).format("YYYY-MM-DD")));
    },
    []
  );

  const changeEndDateMemoized = useCallback(
    (date) => {
      dispatch(changeEndDate(dayjs(date).format("YYYY-MM-DD")));
    },
    []
  );

  const clearSnackbar = useCallback(() => {
    dispatch(clearError());
  }, []);

  const clearSnackbarSuccess = useCallback(() => {
    dispatch(clearSuccess());
  }, []);

  const setSnackbar = useCallback((error) => {
    dispatch(setError(error));
  }, []);

  const handleSubmit = () => {
    if (!startDate) {
      setSnackbar("No se ha seleccionado fecha de inicio.");
      return;
    }
    if (!endDate) {
      setSnackbar("No se ha seleccionado fecha final.");
      return;
    }
    if (!stationSelected) {
      setSnackbar("No se ha seleccionado alguna planta.");
      return;
    }
    if (!dayjs(startDate).isSameOrBefore(dayjs(endDate), "day")) {
      setSnackbar("La fecha final no puede ser menor a la inicial.");
      return;
    }
    if (dayjs(endDate).diff(dayjs(startDate), "day") > 15) {
      setSnackbar("El lapso entre fechas no puede ser mayor a 15 días.");
      return;
    }
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    fetchOrders(stationSelected, start, end, typeCustomer?.value);
  };

  const handleDropdownItem = (event, info) => {
    switch (event) {
      case menuOptions.select:
        addOrders(info);
        return;
      case menuOptions.deselect:
        removeOrders(info);
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    if (dayjs(endDate).diff(dayjs(startDate), "day") > 7 || dayjs(endDate).isBefore(dayjs(startDate), "day")) {
      changeEndDateMemoized(startDate);
    }
  }, [endDate, startDate]);

  useEffect(() => {
    setMapStations(convertToMapById(stations));
  }, [stations]);

  return (
    <React.Fragment>
      <SelectDateMenu
        onChangeStartDate={changeStartDateMemoized}
        onChangeEndDate={changeEndDateMemoized}
        stations={mapStations}
        optionSelected={stationSelected}
        dates={{ startDate, endDate }}
        stationSelected={stationSelected}
        onChangeFilter={changeStationMemoized}
        hasStations
        onSubmit={handleSubmit}
        color="info"
        publicGeneral
        onChangeInputCustomer={changeTypeCustomer}
        customer={typeCustomer}
      />

      <CardTable
        data={orders}
        columns={configTable}
        loading={isLoadingOrders}
        previousText="Anterior"
        nextText="Siguiente"
        noDataText="Sin datos"
        pageText="Pagina"
        ofText="de"
        resizable={false}
        rowsText="pedidos"
      >
        {(state, makeTable) => {
          return (
            <>
              <Link to="/maps/ordenes_mapa">
                <Button className={classes.mapButton} color="info" round>
                  <Map /> Abrir mapa
                </Button>
              </Link>

              <CustomDropdown
                dropup
                buttonText="Acciones "
                hoverColor="info"
                justify="end"
                buttonProps={{
                  round: true,
                  color: "info",
                }}
                onClick={(event) => handleDropdownItem(event, state.pageRows)}
                dropdownList={[menuOptions.select, menuOptions.deselect]}
              />
              {makeTable()}
            </>
          );
        }}
      </CardTable>
      <Snackbars
        place="tr"
        color="danger"
        message={errorMessage}
        open={error}
        closeNotification={() => clearSnackbar()}
        close
      />
      <Snackbars
        place="tr"
        color={colorSnackbar}
        message={message}
        open={success}
        closeNotification={() => clearSnackbarSuccess()}
        close
      />
    </React.Fragment>
  );
}
